import { Bell, BellOff } from "lucide-react";
import { PUSH_PANEL_STATES, PUSH_ACTION_KEYS, pushPanelAction, pushPanelCopy } from "../pushSettings.js";
import { t } from "../strings.js";

// P4-1/P4-2 — **THE PANEL SHE READS AND THE ONE CONTROL SHE TAPS.**
//
// Hookless (R7/R8's entry requirement), so `scripts/p4-push-settings.mjs` CALLS it once per state
// and reads the tree back. The state and the three async steps live in `PushSettings.tsx`, which is
// the `PushOptIn`/`PushOptInSheet` split one control over and for the same reason: the reason this
// door was reported missing in the first place is that nothing could see it.
//
// **FOUR OF THE SIX STATES DRAW NO BUTTON, AND THAT IS ARITHMETIC RATHER THAN LAYOUT** — it comes
// straight off `pushPanelAction`, which is P4-4's ruling written down: an unsupported context says
// so honestly rather than offering a moment that cannot complete. A control that cannot do its own
// job teaches her to distrust the ones that can, which is R35's clipped-confirm lesson one size up.
//
// **THE FACE SAYS WHAT IS; THE BUTTON SAYS WHAT A TAP DOES** (ITEM 7's law). The heading and the
// sentence report the state, and the button is "Turn on" / "Turn off", never a toggle labelled with
// the thing it is already showing.
export function PushSettingsPanel({
  state, busy = false, note = "", language = "en", onAct,
}: {
  // R18's third state is a real value here, not a missing prop: `unknown` is what this panel draws
  // while it is still reading the device, and drawing OFF there tells a subscribed member she is
  // unsubscribed and invites her to fix something that is not broken.
  state: string;
  busy?: boolean;
  note?: string;
  language?: string;
  onAct: (action: string) => void;
}) {
  const copy = pushPanelCopy(state);
  const action = pushPanelAction(state);
  const on = state === PUSH_PANEL_STATES.ON;
  return (
    <div className={`push-settings push-settings--${state}`}>
      <h3 className="push-settings__head">
        {on ? <Bell size={15} aria-hidden="true" /> : <BellOff size={15} aria-hidden="true" />}
        {" "}{t(language, "push.heading")}
      </h3>
      <p className="push-settings__state">{t(language, copy.title)}</p>
      {/* S7-1 — **THE SECOND LINE IS A REPAIR OR IT IS NOTHING.** Her ruling killed the ON/OFF
          explainer outright; what is left is drawn only where `pushPanelTeaches` says there is
          somewhere else to go, and the roster's `note: null` is what carries that to the screen. An
          unrostered state falls back to `unknown`'s row and so draws none, which is the honest
          answer for a state nobody declared (R44: refuse, never resolve). */}
      {copy.note ? <p className="push-settings__note">{t(language, copy.note)}</p> : null}
      {action ? (
        <button
          type="button"
          className={`push-settings__act push-settings__act--${action}`}
          onClick={() => onAct(action)}
          disabled={busy}
        >
          {busy ? t(language, "push.working") : t(language, PUSH_ACTION_KEYS[action])}
        </button>
      ) : null}
      {/* R47/R53/R61: the answer is READ and a refusal is SAID. A control that swallows its own
          failure leaves a truthful-looking screen, which is the one shape nobody reports. */}
      {note ? <p className="push-settings__note push-settings__note--warn">{note}</p> : null}
    </div>
  );
}
